const EMPTY_DISPLAY = '-'
const BE_OFFSET = 543
/** Years above this are treated as Buddhist Era (พ.ศ.) */
const BE_YEAR_THRESHOLD = 2400

function parseDateInput(value: Date | string | null | undefined): Date | null {
  if (value == null || value === '') {
    return null
  }

  const parsedDate = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(parsedDate.getTime())) {
    return null
  }

  return parsedDate
}

/** Convert Buddhist Era year to Christian Era when value looks like พ.ศ. */
export function toChristianYear(year: number): number {
  return year > BE_YEAR_THRESHOLD ? year - BE_OFFSET : year
}

/**
 * Normalize an ISO date/datetime string so the year is Christian Era (ค.ศ.).
 * e.g. "2567-07-21" → "2024-07-21", "2567-07-21T15:37" → "2024-07-21T15:37"
 */
export function normalizeIsoDateYearToChristian(value: string): string {
  if (!value?.trim()) {
    return value
  }

  const match = value.trim().match(/^(\d{4})(.*)$/)
  if (!match) {
    return value
  }

  const year = Number(match[1])
  const ceYear = toChristianYear(year)
  if (ceYear === year) {
    return value.trim()
  }

  return `${String(ceYear).padStart(4, '0')}${match[2]}`
}

/**
 * Normalize filter date/datetime values for SQL Server.
 * e.g. "2567-07-21T15:37" → "2024-07-21 15:37:00"
 */
export function toSqlServerDateTime(value: string): string {
  if (!value?.trim()) {
    return value
  }

  const christian = normalizeIsoDateYearToChristian(value.trim())

  if (/^([01]\d|2[0-3]):([0-5]\d)$/.test(christian)) {
    return christian
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(christian)) {
    return christian
  }

  const datetimeMatch = christian.match(
    /^(\d{4}-\d{2}-\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/,
  )
  if (datetimeMatch) {
    const seconds = datetimeMatch[4] ?? '00'
    return `${datetimeMatch[1]} ${datetimeMatch[2]}:${datetimeMatch[3]}:${seconds}`
  }

  return christian
}

/**
 * Return a Date whose year is Christian Era (ค.ศ.).
 * Flatpickr can pick up พ.ศ. from the year input and produce year 2567 etc.
 */
export function toChristianEraDate(date: Date): Date {
  const year = date.getFullYear()
  const ceYear = toChristianYear(year)
  if (ceYear === year) {
    return date
  }

  const normalized = new Date(date.getTime())
  normalized.setFullYear(ceYear)
  return normalized
}

/** e.g. 12 ก.ค. 2569 */
export function formatDateThai(value: Date | string | null | undefined): string {
  const parsedDate = parseDateInput(value)
  if (!parsedDate) {
    return EMPTY_DISPLAY
  }

  return toChristianEraDate(parsedDate).toLocaleDateString('th-TH', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'Asia/Bangkok',
  })
}

/** e.g. 12 ก.ค. 2026 — Thai month labels with Christian Era year */
export function formatDateThaiChristian(
  value: Date | string | null | undefined,
): string {
  const parsedDate = parseDateInput(value)
  if (!parsedDate) {
    return EMPTY_DISPLAY
  }

  return toChristianEraDate(parsedDate).toLocaleDateString('th-TH-u-ca-gregory', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'Asia/Bangkok',
  })
}

/** e.g. 12:05 น. */
export function formatTimeThai(value: Date | string | null | undefined): string {
  const parsedDate = parseDateInput(value)
  if (!parsedDate) {
    return EMPTY_DISPLAY
  }

  const timeStr = parsedDate.toLocaleTimeString('en-GB', {
    timeZone: 'Asia/Bangkok',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
  return `${timeStr} น.`
}

/** e.g. 12 ก.ค. 2569 12:05 น. */
export function formatDateTimeThai(value: Date | string | null | undefined): string {
  const parsedDate = parseDateInput(value)
  if (!parsedDate) {
    return EMPTY_DISPLAY
  }

  return `${formatDateThai(parsedDate)} ${formatTimeThai(parsedDate)}`
}

const MINUTE_MS = 60_000
const HOUR_MS = 60 * MINUTE_MS
const DAY_MS = 24 * HOUR_MS
const WEEK_MS = 7 * DAY_MS
const MONTH_MS = 30 * DAY_MS
const YEAR_MS = 365 * DAY_MS

/**
 * Relative Thai time for display under absolute datetimes.
 * e.g. "เมื่อสักครู่", "5 นาทีที่แล้ว", "2 ชั่วโมงที่แล้ว", "3 วันที่แล้ว"
 * Returns empty string when value is missing/invalid.
 */
export function formatRelativeTimeThai(
  value: Date | string | null | undefined,
  now: Date = new Date(),
): string {
  const parsedDate = parseDateInput(value)
  if (!parsedDate) {
    return ''
  }

  const diffMs = now.getTime() - parsedDate.getTime()
  if (diffMs < 0) {
    return ''
  }

  if (diffMs < MINUTE_MS) {
    return 'เมื่อสักครู่'
  }

  if (diffMs < HOUR_MS) {
    const minutes = Math.floor(diffMs / MINUTE_MS)
    return `${minutes} นาทีที่แล้ว`
  }

  if (diffMs < DAY_MS) {
    const hours = Math.floor(diffMs / HOUR_MS)
    return `${hours} ชั่วโมงที่แล้ว`
  }

  if (diffMs < WEEK_MS) {
    const days = Math.floor(diffMs / DAY_MS)
    return `${days} วันที่แล้ว`
  }

  if (diffMs < MONTH_MS) {
    const weeks = Math.floor(diffMs / WEEK_MS)
    return `${weeks} สัปดาห์ที่แล้ว`
  }

  if (diffMs < YEAR_MS) {
    const months = Math.max(1, Math.floor(diffMs / MONTH_MS))
    return `${months} เดือนที่แล้ว`
  }

  const years = Math.max(1, Math.floor(diffMs / YEAR_MS))
  return `${years} ปีที่แล้ว`
}

/** e.g. 12 ก.ค. 2026 12:05 น. */
export function formatDateTimeThaiChristian(
  value: Date | string | null | undefined,
): string {
  const parsedDate = parseDateInput(value)
  if (!parsedDate) {
    return EMPTY_DISPLAY
  }

  return `${formatDateThaiChristian(parsedDate)} ${formatTimeThai(parsedDate)}`
}
